import { CapacityPerformance } from "../../../features/Reports/typings/reports.types";
import { VehicleCapacityReportItem } from "../../../features/Reports/typings/fleet-capacity-report.types";
import { FleetManagementRow } from "../../FleetManagement/types/FleetManagement-row.types";
import { FleetStatus } from "../../FleetManagement/types/FleetManagement.types";
import { LocationInventory } from "../../../features/FloorManager/typings/location-inventory";

/**
 * Formats a capacity rate (0–1) as a percentage string for display (e.g. "13.6%").
 * Returns "" for null, undefined, non-number, or NaN.
 */
export function formatCapacityPercent(value: number | undefined | null): string {
  if (value == null || typeof value !== "number" || Number.isNaN(value)) {
    return "0.0%";
  }
  return `${(value * 100).toFixed(1)}%`;
}

/**
 * Computes the next selectedVehicleIds after a fleet Calculate (and refetch).
 * Preserves the user's selection when it is still a valid subset of idsToUse (refetched list).
 * Resets to idsToUse when there was no selection or some selected IDs are no longer in the list.
 */
export function computeFleetReportIdsAfterCalculate(
  idsToUse: number[],
  prevSelectedVehicleIds: number[]
): { nextSelectedVehicleIds: number[] } {
  const allCurrentInNew =
    prevSelectedVehicleIds.length > 0 && prevSelectedVehicleIds.every(id => idsToUse.includes(id));
  const nextSelectedVehicleIds =
    prevSelectedVehicleIds.length === 0 || !allCurrentInNew ? idsToUse : prevSelectedVehicleIds;
  return { nextSelectedVehicleIds };
}

/**
 * Returns vehicle inventory IDs that match the given department filter.
 * When departmentIds is empty, all vehicles are returned. Otherwise only vehicles whose
 * affectedDepartments include at least one of the given department IDs are returned.
 */
export function getVehicleIdsMatchingFilter(
  vehicles: FleetManagementRow[],
  departmentIds: string[]
): number[] {
  if (vehicles.length === 0) return [];

  const departmentIdsSet = new Set(departmentIds);
  const hasDepartmentFilter = departmentIdsSet.size > 0;

  if (!hasDepartmentFilter) return vehicles.map(v => v.id);

  return vehicles
    .filter(v => {
      const fromAffected = (v.affectedDepartments ?? []).map(String);
      const vehicleDepartmentIds = new Set(fromAffected);
      const hasMatch = Array.from(departmentIdsSet).some(did =>
        vehicleDepartmentIds.has(String(did))
      );
      if (!hasMatch) return false;
      return true;
    })
    .map(v => v.id);
}

/** Empty capacity report when no vehicles are selected; used to show empty charts/table. */
export const EMPTY_CAPACITY_PERFORMANCE: CapacityPerformance = {
  capacityPerformancePerDay: [],
  capacityPerformancePerWorkplace: [],
  capacityPerformancePerHour: [],
  averageCapacityPerformancePerDay: 0,
  averageCapacityPerformancePerWorkplace: 0,
  averageCapacityPerformancePerWeekday: {
    monday: 0,
    tuesday: 0,
    wednesday: 0,
    thursday: 0,
    friday: 0,
    saturday: 0,
    sunday: 0
  },
  dayStandardDeviation: 0,
  distinctTotalUsers: [],
  uniqueInventoryIds: []
};

export type CapacityObjectMapped = {
  id: number | string;
  data: CapacityPerformance;
  label: string | string[];
};

/**
 * Maps fleet capacity report response to the shape expected by AreaChartsReport,
 * WeekdayBars, and CapacityPerHourReport (same as facility report).
 */
export function mapFleetCapacityToCapacityObjects(
  capacity: CapacityPerformance | undefined | null,
  label: string
): CapacityObjectMapped[] {
  if (!capacity) return [];
  return [
    {
      id: 0,
      data: capacity,
      label
    }
  ];
}

/**
 * Maps fleet capacity report response to entityData shape for WeekdayBars
 * (array of { id, capacity }).
 */
export function mapFleetCapacityToEntityData(
  capacity: CapacityPerformance | undefined | null
): { id: number; capacity: CapacityPerformance }[] {
  if (!capacity) return [];
  return [{ id: 0, capacity }];
}

const EMPTY_FLEET_ROW_PLACEHOLDER: Pick<
  FleetManagementRow,
  "fleetPermissions" | "affectedCompanies" | "affectedDepartments" | "affectedUsers" | "properties"
> = {
  fleetPermissions: {
    activeWeekdays: [],
    companies: [],
    costCenterUnits: [],
    users: [],
    ownerId: null
  },
  affectedCompanies: [],
  affectedDepartments: [],
  affectedUsers: [],
  properties: []
};

/** Optional fields that the entities API may return for filtering (e.g. in tests). */
type EntityVehicleWithDepartments = VehicleCapacityReportItem & {
  affectedDepartments?: string[];
  fleetPermissions?: { costCenterUnits?: { id: string }[] };
};

/** Map API property shape to FleetManagementRow.properties (BookingProperty-compatible). */
function mapReportPropertiesToRow(
  properties: VehicleCapacityReportItem["properties"]
): FleetManagementRow["properties"] {
  if (!properties?.length) return [];
  return properties.map(p => ({
    id: p.id,
    name: p.name,
    description: ""
  }));
}

/**
 * Maps fleet-capacity-report-entities API vehicles to FleetManagementRow for the report table and filters.
 * If the API returns affectedDepartments or fleetPermissions.costCenterUnits, they are passed through for filtering.
 * If the API returns properties, they are mapped for the table Properties column.
 */
export function mapEntitiesVehiclesToFleetRows(
  vehicles: VehicleCapacityReportItem[],
  allLocations: LocationInventory[]
): FleetManagementRow[] {
  return vehicles.map(v => {
    const id = v.vehicleInventoryId ?? v.id;
    const withDepts = v as EntityVehicleWithDepartments;
    const properties = mapReportPropertiesToRow(v.properties);
    return {
      id,
      name: v.name,
      description: v.description ?? null,
      picturePaths: v.picturePaths ?? null,
      licensePlate: v.licensePlate,
      brandModel: v.brandModel,
      vehicleType: v.vehicleType ?? null,
      fuel: v.fuel,
      reach: v.reach,
      numSeats: v.numSeats,
      status: FleetStatus.Available,
      location: allLocations.find(l => l.id === v.locationInventoryId),
      ...EMPTY_FLEET_ROW_PLACEHOLDER,
      ...(properties.length > 0 && { properties }),
      ...(withDepts.affectedDepartments && {
        affectedDepartments: withDepts.affectedDepartments
      }),
      ...(withDepts.fleetPermissions?.costCenterUnits && {
        fleetPermissions: {
          ...EMPTY_FLEET_ROW_PLACEHOLDER.fleetPermissions,
          costCenterUnits: withDepts.fleetPermissions.costCenterUnits
        }
      })
    } as FleetManagementRow;
  });
}
